import type { MediaType, Role } from "@/lib/types";

export const DAYS = [
  { v: 1, short: "Lun", long: "Lundi" },
  { v: 2, short: "Mar", long: "Mardi" },
  { v: 3, short: "Mer", long: "Mercredi" },
  { v: 4, short: "Jeu", long: "Jeudi" },
  { v: 5, short: "Ven", long: "Vendredi" },
  { v: 6, short: "Sam", long: "Samedi" },
  { v: 7, short: "Dim", long: "Dimanche" },
];

export const TIMEZONES = [
  "America/Toronto",
  "America/Montreal",
  "America/Halifax",
  "America/Moncton",
  "America/St_Johns",
  "America/Winnipeg",
  "America/Regina",
  "America/Edmonton",
  "America/Vancouver",
  "America/New_York",
  "Europe/Paris",
  "UTC",
];

export const ROLE_LABELS: Record<Role, string> = {
  super_admin: "Super-admin",
  organization_admin: "Administrateur",
  editor: "Éditeur",
};

export const MEDIA_LABELS: Record<MediaType, string> = {
  image: "Image",
  video: "Vidéo",
  web: "Page Web",
  text: "Texte",
  rss: "Flux RSS",
  weather: "Météo",
  clock: "Horloge",
};

export const BANNER_FONTS: Record<string, { label: string; css: string }> = {
  sans: { label: "Moderne (sans empattement)", css: "'IBM Plex Sans', Arial, sans-serif" },
  serif: { label: "Classique (serif)", css: "Georgia, 'Times New Roman', serif" },
  mono: { label: "Machine à écrire", css: "'JetBrains Mono', monospace" },
  condensed: { label: "Condensée", css: "'Arial Narrow', 'Roboto Condensed', sans-serif" },
  script: { label: "Calligraphie", css: "'Brush Script MT', 'Segoe Script', cursive" },
  casual: { label: "Décontractée", css: "'Comic Sans MS', 'Comic Neue', cursive" },
};

export const DEFAULT_BANNER_STYLE = { font: "sans", size: "medium", color: "#FFFFFF", bg: "#000000", bg_opacity: 75, speed: "normal", position: "bottom", rss_url: null } as const;

export const SUB_LABELS: Record<string, string> = {
  trial: "Essai",
  active: "Actif",
  past_due: "En retard",
  suspended: "Suspendu",
  cancelled: "Annulé",
};

export const ORIENTATIONS = [
  { v: "landscape", label: "Paysage", deg: 0 },
  { v: "portrait", label: "Portrait", deg: 90 },
  { v: "landscape_reverse", label: "Paysage inversé", deg: 180 },
  { v: "portrait_reverse", label: "Portrait inversé", deg: 270 },
] as const;

export function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("fr-CA", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
}

export function fmtRelative(iso?: string | null): string {
  if (!iso) return "Jamais";
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "À l'instant";
  if (s < 3600) return `Il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `Il y a ${Math.floor(s / 3600)} h`;
  return `Il y a ${Math.floor(s / 86400)} j`;
}

export function fmtDuration(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m ? `${m} min ${s.toString().padStart(2, "0")} s` : `${s} s`;
}

export function fmtBytes(n?: number | null): string {
  if (!n) return "";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}

export function daysLabel(days: number[]): string {
  if (days.length === 7) return "Tous les jours";
  if (days.join(",") === "1,2,3,4,5") return "Lun – Ven";
  if (days.join(",") === "6,7") return "Fin de semaine";
  return days.map((d) => DAYS[d - 1]?.short).join(", ");
}
